/**
 * RecordSignalStrip - the horizontal "signal" strip shown at the top of a
 * record detail page (registration / warranty / transfer). A row of
 * uppercase label + value + caption cells inside one bordered card.
 *
 * Extracted from warranty-record-page.tsx so every record detail page
 * shares one implementation instead of re-declaring page-local copies.
 */

import type { ReactNode } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'

export function RecordSignalStrip({
  children,
  className,
  gridClassName,
}: {
  children: ReactNode
  /** Extra classes (e.g. margins) appended after the base + grid. */
  className?: string
  /** Override the responsive grid column classes (default: up to 6 cols). */
  gridClassName?: string
}) {
  return (
    <section
      className={cn(
        'grid overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] shadow-[0_12px_34px_rgba(26,26,24,0.04)]',
        gridClassName ?? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
        className,
      )}
    >
      {children}
    </section>
  )
}

export function RecordSignal({
  label,
  value,
  sub,
  icon,
}: {
  label: string
  value: ReactNode
  /** Caption under the value. Optional - the cell keeps its height either
   *  way, so an absent caption does not break the strip's alignment. */
  sub?: string
  icon?: ReactNode
}) {
  return (
    <div className="min-w-0 border-b-[0.5px] border-b-[var(--rule-light)] px-5 py-4 last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0 sm:[&:nth-child(odd)]:border-r-[0.5px] sm:[&:nth-child(odd)]:border-r-[var(--rule-light)] lg:[&:nth-child(odd)]:border-r-0 xl:border-b-0 xl:border-r-[0.5px] xl:border-r-[var(--rule-light)] xl:last:border-r-0">
      <div className="mb-2 flex items-center gap-1.5 text-[9.5px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {icon ? <span className="text-[var(--ink-3)]">{icon}</span> : null}
        <span>{label}</span>
      </div>
      <div className="flex min-h-7 items-center font-sans text-[14px] font-medium text-[var(--ink)]">
        {value}
      </div>
      <div className="mt-1.5 truncate text-[11.5px] leading-[1.4] text-[var(--ink-3)]">
        {sub}
      </div>
    </div>
  )
}

export function SignalBadge({
  children,
  tone,
}: {
  children: ReactNode
  tone: 'success' | 'warning' | 'gold' | 'neutral'
}) {
  const toneClassName =
    tone === 'success'
      ? 'border-transparent bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
      : tone === 'warning'
        ? 'border-transparent bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
        : tone === 'gold'
          ? 'border-transparent bg-[var(--gold-bg-muted)] text-[var(--gold-ink)]'
          : 'border-transparent bg-[var(--bg-subtle)] text-[var(--ink-2)]'

  return (
    <Badge
      variant="outline"
      className={[
        'h-[24px] max-w-full rounded-[3px] px-2.5 text-[10px] font-normal uppercase tracking-[0.08em] shadow-none',
        toneClassName,
      ].join(' ')}
    >
      <span className="truncate">{children}</span>
    </Badge>
  )
}
